// 치과용 「데스크 접수 처리위탁 확인」 카드 (2026-10-04 대표 결정 — 확인한 치과만 QR 접수를 받는다, 서버 clinic-consent-required)
// 정본: docs/정본_데스크접수_동의·방침_2026-10-04.md §7-6 (desk-clinic-1.1, QR 접수) · §8-2 (desk-clinic-1.2, + 데스크 직접 입력).
//   원문·판·해시는 utils/deskReception.js DESK_CLINIC_CONSENTS 표. 서버가 알려 준 판·해시와 같은 원문을 골라 보인다(웹·서버 배포 순서가 어긋나도 맞는 판만 보냄).
//   원문은 바이트 그대로(줄 배열 join('\n')) pre-wrap 으로 보인다. 원문 마지막 줄의 '☐'는 해시 대상이라 바꾸지 않는다 —
//   대신 실제 체크박스는 원문 블록 아래 강조 칸에 "(체크)" 라벨로 따로 둬서 ☐가 두 번 보여 헷갈리지 않게 한다(10-04).
// 대표(canConfirm)만 체크·확인. 직원은 원문을 읽고 "대표가 확인해야 합니다" 안내만.
// variant:
//   'gate'(기본) — QR 접수 게이트. 사용처: ClinicWalkinQr([QR 만들기] 앞), DeskReception(맨 위 — 미확인이면 카드)
//       showReconfirm=false 면 1.1 확인 치과(confirmed·isCurrent:false)에 재확인 카드를 그리지 않는다 — 1.1 로 QR 접수는 그대로 되므로
//       데스크 화면을 막지 않고, 1.2 확인은 직접 추가 영역(variant 'deskAdd')에서만 묻는다(10-04 직접 추가).
//   'deskAdd' — 데스크 「직접 추가」 영역. 서버 deskAddReady 면 아무것도 안 그림, 아니면 deskAddVersion(1.2) 원문 확인 카드
// onState({ loading, confirmed, isCurrent, canConfirm, deskAddReady, deskAddFrom, deskAddVersion }) — 판정 불가(서버 미배포·오류)면 false/null(fail-closed)
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getDeskClinicConsent, acceptDeskClinicConsent } from '../../services/receptionApi';
import { DESK_CLINIC_CONSENT, DESK_CLINIC_CONSENTS, deskClinicConsentFor, deskClinicConsentText, receptionDeskError } from '../../utils/deskReception';

function fmtDate(ms) {
    if (!Number.isFinite(Number(ms))) return '';
    const k = new Date(Number(ms) + 9 * 3600 * 1000);
    return `${k.getUTCFullYear()}.${k.getUTCMonth() + 1}.${k.getUTCDate()}`;
}

function DeskClinicConsentCard({ onState, showConfirmedLine = false, showReconfirm = true, variant = 'gate' }) {
    // 훅은 전부 최상단 — early return 위 (Dashboard 사고)
    const [st, setSt] = useState({ loading: true, data: null, err: '' });
    const [checked, setChecked] = useState(false);
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState('');
    const onStateRef = useRef(onState);
    onStateRef.current = onState;

    const load = useCallback(async () => {
        setSt(s => ({ ...s, loading: true, err: '' }));
        try {
            const r = await getDeskClinicConsent();
            setSt({ loading: false, data: r || {}, err: '' });
        } catch (e) {
            console.error('[접수] 처리위탁 확인 조회 실패:', e?.code || e?.message || e, e?.details?.code || '');
            setSt({ loading: false, data: null, err: receptionDeskError(e, '처리위탁 확인 상태를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.') });
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const d = st.data || {};
    const confirmed = d.confirmed === true;
    const isCurrent = d.isCurrent === true;
    const deskAddReady = d.deskAddReady === true;
    const deskAddFrom = typeof d.deskAddFrom === 'string' && d.deskAddFrom ? d.deskAddFrom : null;
    const deskAddVersion = typeof d.deskAddVersion === 'string' && d.deskAddVersion ? d.deskAddVersion : null;
    const canConfirm = d.canConfirm === true;
    useEffect(() => {
        if (typeof onStateRef.current !== 'function') return;
        const done = !st.loading;
        onStateRef.current({
            loading: st.loading,
            confirmed: done && confirmed,
            isCurrent: done && isCurrent,
            canConfirm: done && canConfirm,
            deskAddReady: done && deskAddReady,
            deskAddFrom: done ? deskAddFrom : null,
            deskAddVersion: done ? deskAddVersion : null,
        });
    }, [st.loading, confirmed, isCurrent, canConfirm, deskAddReady, deskAddFrom, deskAddVersion]);

    // 확인할 판 — gate: 서버 현재 판 / deskAdd: 서버가 직접 추가에 요구하는 판. 웹 표에 바이트 동일한 원문이 없으면 보내지 않는다
    const target = variant === 'deskAdd'
        ? (deskAddVersion
            ? (deskAddVersion === d.current
                ? deskClinicConsentFor(d.current, d.currentTextHash)
                : (DESK_CLINIC_CONSENTS.find(c => c.version === deskAddVersion) || null))
            : null)
        : (st.data ? deskClinicConsentFor(d.current, d.currentTextHash) : DESK_CLINIC_CONSENT);
    const sameVersion = !!target;
    const needReconfirm = confirmed && !isCurrent;

    const accept = async () => {
        if (busy || !checked || !canConfirm || !target) return;
        setBusy(true); setNotice('');
        try {
            const r = await acceptDeskClinicConsent(target.version, target.textHash);
            if (r?.ok !== true) {
                console.error('[접수] 처리위탁 확인 응답 이상: ok=', r?.ok);
                setNotice('확인을 기록하지 못했습니다. 잠시 후 다시 눌러 주세요.');
                return;
            }
            setChecked(false);
            await load();
        } catch (e) {
            console.error('[접수] 처리위탁 확인 실패:', e?.code || e?.message || e, e?.details?.code || '');
            setNotice(receptionDeskError(e, '확인을 기록하지 못했습니다. 잠시 후 다시 눌러 주세요.'));
        } finally {
            setBusy(false);
        }
    };

    // 조회 중에는 아무것도 그리지 않는다(10-08 — 확인 끝난 치과에 카드가 잠깐 비치지 않게). 상태 전달(onState loading)은 위에서 그대로
    if (st.loading) return null;

    if (st.err) {
        return (
            <div style={C.box}>
                <div style={C.errNote}>{st.err}</div>
                <button type="button" onClick={load} style={{ ...C.btnS, marginTop: 8 }}>다시 불러오기</button>
            </div>
        );
    }

    if (variant === 'deskAdd') {
        if (deskAddReady) return null;
        // 서버에 직접 추가용 판(1.2) 원문이 아직 없음 — 확인할 것이 없다
        if (!deskAddVersion) return <div style={C.box}><div style={C.sub}>데스크 직접 추가는 아직 준비 중입니다.</div></div>;
    } else if (confirmed && (!needReconfirm || !showReconfirm)) {
        if (!showConfirmedLine) return null;
        return <div style={C.okLine}>{`데스크 접수 처리위탁 확인 완료${fmtDate(d.at) ? ` (${fmtDate(d.at)}, ${d.version || ''})` : ''}`}</div>;
    }

    const shown = target || DESK_CLINIC_CONSENT;
    let title; let sub; let btn;
    if (variant === 'deskAdd') {
        title = '직접 추가를 쓰려면 처리위탁 확인(새 판)이 필요합니다';
        sub = confirmed
            ? '데스크에서 직접 넣은 환자의 이름·오신 이유도 덴탈클러스터가 맡아 처리하므로, 치과 대표가 아래 새 판을 확인해야 직접 추가를 쓸 수 있습니다. QR 접수는 지금처럼 계속 받습니다.'
            : '치과 대표가 아래 내용을 확인하면 QR 접수와 데스크 직접 추가를 쓸 수 있습니다.';
        btn = '확인하고 직접 추가 시작';
    } else {
        title = needReconfirm ? '데스크 접수 처리위탁 확인 — 내용이 바뀌어 다시 확인이 필요합니다' : '데스크 접수 처리위탁 확인';
        sub = needReconfirm
            ? '이전 판 확인으로 QR 접수는 계속 받습니다. 바뀐 내용을 읽고 다시 확인해 주세요.'
            : '치과 대표가 아래 내용을 확인해야 환자가 QR로 접수할 수 있습니다. 확인 전에는 QR 접수가 시작되지 않습니다.';
        btn = needReconfirm ? '다시 확인' : '확인하고 QR 접수 시작';
    }

    return (
        <div style={C.box}>
            <div style={C.title}>{title}</div>
            <div style={C.sub}>{sub}</div>
            <div style={C.text}>{deskClinicConsentText(shown)}</div>
            {!sameVersion ? (
                <div style={C.errNote}>확인 내용이 최신이 아닙니다. 화면을 새로고침해 주세요.</div>
            ) : canConfirm ? (
                <>
                    <div style={C.agreeHint}>위 글 마지막 줄의 네모(☐)는 문안의 일부입니다. 확인은 아래 칸을 눌러 체크해 주세요.</div>
                    <label style={{ ...C.agree, ...(checked ? C.agreeOn : null) }}>
                        <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)} style={{ width: 20, height: 20, flexShrink: 0, margin: 0, accentColor: '#0f6b5c' }} />
                        <span style={{ fontWeight: 700 }}>위 내용을 확인했습니다(체크)</span>
                    </label>
                    <button type="button" disabled={!checked || busy} onClick={accept} style={{ ...C.btnP, opacity: !checked || busy ? 0.5 : 1 }}>
                        {busy ? '기록 중…' : btn}
                    </button>
                </>
            ) : (
                <div style={C.readonly}>치과 대표가 확인해야 합니다. 대표 계정으로 로그인해 이 화면에서 확인해 주세요.</div>
            )}
            {notice && <div style={C.errNote}>{notice}</div>}
        </div>
    );
}

const C = {
    box: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 18px', marginBottom: 14 },
    title: { fontSize: 15, fontWeight: 800, color: '#0f172a', marginBottom: 4 },
    sub: { fontSize: 12.5, color: '#64748b', lineHeight: 1.6 },
    text: { marginTop: 10, fontSize: 13, color: '#1e293b', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'keep-all', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px', maxHeight: 340, overflowY: 'auto' },
    agreeHint: { marginTop: 10, fontSize: 12, color: '#64748b', lineHeight: 1.5 },
    agree: { display: 'flex', gap: 10, alignItems: 'center', fontSize: 14, color: '#0f172a', marginTop: 6, cursor: 'pointer', background: '#f0fdf9', border: '2px solid #0f6b5c', borderRadius: 10, padding: '11px 14px' },
    agreeOn: { background: '#d1fae5' },
    btnP: { marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', background: '#0f6b5c', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13.5, fontWeight: 700, cursor: 'pointer' },
    btnS: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: '#fff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
    readonly: { marginTop: 10, fontSize: 12.5, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 11px' },
    errNote: { marginTop: 10, fontSize: 12.5, color: '#8a2020', background: '#fdecec', border: '1px solid #f5cfcf', borderRadius: 8, padding: '8px 11px', lineHeight: 1.55 },
    okLine: { fontSize: 12.5, color: '#065f46', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, padding: '8px 11px', marginBottom: 14 },
};

export default DeskClinicConsentCard;
